<template>
  <!-- Vai para o `body`: a tela do app é um contexto de empilhamento próprio, e dentro
       dele a barra de abas pintava por cima da folha — o rodapé dela ficava cortado.
       Fora, ela cobre a tela inteira, que é o que uma folha modal precisa fazer. -->
  <Teleport to="body">
    <!-- Ajuda é consulta, aberta pelo botão. Ela não mexe em preferência nenhuma: quem
         decide se o tutorial abre sozinho é o tutorial, e só na primeira visita. -->
    <div class="scrim" @click.self="$emit('close')">
      <div class="sheet" role="dialog" aria-modal="true" aria-label="Ajuda">
        <div class="grab" />

        <header>
          <div class="crest"><AppIcon name="link" size="lg" /></div>
          <div>
            <h3>Ajuda</h3>
            <p>Cinco palavras ligadas por sentido. Você adivinha quatro.</p>
          </div>
        </header>

        <div class="tabs" role="tablist">
          <button
            v-for="tab in tabs"
            :key="tab.id"
            class="tab"
            :class="{ on: tab.id === current }"
            type="button"
            role="tab"
            :aria-selected="tab.id === current"
            @click="current = tab.id"
          >
            <AppIcon :name="tab.icon" size="sm" />{{ tab.label }}
          </button>
        </div>

        <!-- ── como jogar ────────────────────────────────────────────────── -->
        <ol v-if="current === 'play'" class="steps">
          <li v-for="(step, i) in steps" :key="i">
            <span class="n">{{ i + 1 }}</span>
            <div><b>{{ step.title }}</b><span v-html="step.text" /></div>
          </li>
        </ol>

        <!-- ── pontuação ─────────────────────────────────────────────────── -->
        <div v-else-if="current === 'score'" class="block">
          <div class="lbl">A conta</div>
          <div class="formula">
            <span class="chip-good">letras ainda escondidas + 1</span> × 10
          </div>
          <p>
            Acertar cedo vale mais. <b class="warn">Cada erro revela mais uma letra e derruba
            10 pontos</b> — a letra em âmbar é a que você pagou para ver.
          </p>
          <p>
            Errar quando sobra só uma letra escondida encerra a partida. A chain nunca aparece
            inteira.
          </p>
          <p>
            Só a <b>diária</b> soma pontos no ranking. Treino e trilha dão XP e contam partida,
            mas não entram no placar — senão bastaria repetir a chain fácil.
          </p>
        </div>

        <!-- ── duelo ─────────────────────────────────────────────────────── -->
        <div v-else-if="current === 'duel'" class="block">
          <div class="lbl">A mesma chain, contra quem você chamar</div>
          <p>
            Todo mundo da sala joga <b>a mesma chain</b>, mas cada um joga quando quiser — não
            é preciso estar junto. O placar de quem já jogou só aparece quando todos jogarem:
            saber o número do outro antes mudaria a decisão de arriscar.
          </p>

          <div class="lbl">O relógio</div>
          <div class="formula">
            <span class="chip-good">90 segundos</span> para a chain inteira
          </div>
          <p>
            O relógio é do servidor, começa depois da contagem de largada e não para se você
            recarregar a página. <b class="warn">Cada erro custa 10 pontos e 10 segundos.</b>
            Tempo zerado encerra a partida com o que já foi feito.
          </p>

          <div class="lbl">O bônus de tempo</div>
          <div class="formula">
            <span class="chip-good">(1 + letras que você digitou) × segundos restantes</span>
          </div>
          <p>
            Não contam as três últimas letras de cada palavra, nem as que vieram reveladas —
            o bônus paga quem resolveu cedo, não quem teve palavra comprida.
          </p>

          <div class="lbl">Sala e rodadas</div>
          <p>
            <b>Duelo</b> é um contra um; <b>combate</b> é grupo. A sala entre amigos convida
            pela lista; a sala aberta gera um código que entra quem tiver o link, mesmo sem
            conta. Uma sala pode ter várias rodadas: cada rodada é uma chain nova, e os pontos
            <b>somam no placar da sala</b>. Ninguém aceitou ainda? A sala pode ser removida.
            O prazo é de sete dias e só começa a contar depois do aceite — quem não jogar até
            lá perde.
          </p>
        </div>

        <!-- ── modos ─────────────────────────────────────────────────────── -->
        <div v-else class="block">
          <div class="lbl">Onde jogar</div>
          <p v-for="mode in modes" :key="mode.key" class="mode">
            <AppIcon :name="mode.is_daily ? 'calendar' : 'refresh'" size="sm" />
            <span><b>{{ mode.name }}</b> — {{ mode.is_daily ? 'pontua e conta para a sequência' : 'quantas partidas quiser, sem pontuar' }}</span>
          </p>
          <p class="mode">
            <AppIcon name="flag" size="sm" />
            <span><b>Trilha</b> — fases em ordem, com estrelas, energia e capítulos. Dá XP e não
              pontua o ranking.</span>
          </p>
          <p v-if="!modes.length" class="empty">
            Nenhum modo disponível agora — o servidor só oferece modo com chain publicada.
          </p>
        </div>

        <!-- Crédito do dicionário. A licença do acervo de significados é CC BY-SA, e
             atribuição não é cortesia: é condição de uso. Fica na ajuda porque é o lugar
             que cobre todas as telas onde o significado aparece. -->
        <p class="credit lbl">
          significados a partir do
          <a href="https://dicionario-aberto.net" target="_blank" rel="noopener noreferrer">Dicionário Aberto</a>,
          sob licença CC BY-SA
        </p>

        <div class="actions">
          <button v-if="inGame" class="btn btn-outline" type="button" @click="$emit('tutorial')">
            <AppIcon name="eye" size="sm" />Tutorial
          </button>
          <button class="btn btn-cta" type="button" @click="$emit('close')">
            <AppIcon :name="inGame ? 'gamepad' : 'x'" size="sm" />
            {{ inGame ? 'Voltar' : 'Fechar' }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
import AppIcon from '../ui/AppIcon.vue';

/* Aba é dado, não `v-if` espalhado: aba nova — de um modo novo, por exemplo — é uma linha
   aqui mais um bloco no template, e a Frente 7 pede exatamente essa facilidade. */
const ABAS = [
  { id: 'play', label: 'Como jogar', icon: 'link' },
  { id: 'score', label: 'Pontuação', icon: 'star' },
  { id: 'duel', label: 'Duelo', icon: 'zap' },
  { id: 'modes', label: 'Modos', icon: 'grid' },
];

const PASSOS = [
  {
    title: 'A primeira palavra é dada.',
    text: 'Ela é a âncora, e é a única pista da palavra seguinte.',
  },
  {
    title: 'Cada palavra se liga à anterior.',
    text: 'Se a âncora é <i>Cosmos</i>, a próxima pode ser <i>Galáxia</i> — e a partir daí a pista '
      + 'passa a ser <i>Galáxia</i>, não <i>Cosmos</i>.',
  },
  {
    title: 'A primeira letra vem de graça.',
    text: 'O resto você digita. Os quadradinhos mostram quantas letras faltam.',
  },
];

export default {
  name: 'HelpSheet',
  components: { AppIcon },
  props: {
    /** Os modos que o servidor ofereceu — sem lista fixa no código. */
    modes: { type: Array, default: () => [] },
    /** Aba aberta ao abrir. A tela de duelos entra direto na explicação do duelo. */
    initial: { type: String, default: 'play' },
    /** Fora da tela de jogo, o rodapé não oferece tutorial nem "voltar ao jogo". */
    inGame: { type: Boolean, default: true },
  },
  emits: ['close', 'tutorial'],
  data() {
    return { current: this.initial, tabs: ABAS, steps: PASSOS };
  },
};
</script>

<style scoped>
.scrim {
  position: fixed;
  inset: 0;
  background: var(--modal-veil);
  display: flex;
  align-items: flex-end;
  z-index: 30;
  animation: fade 0.22s ease-out;
  backdrop-filter: blur(3px);
}

@keyframes fade { from { opacity: 0; } to { opacity: 1; } }

.sheet {
  width: 100%;
  background: var(--surface);
  border-radius: var(--r-card-lg) var(--r-card-lg) 0 0;
  padding: 20px 18px calc(18px + env(safe-area-inset-bottom));
  display: flex;
  flex-direction: column;
  gap: 14px;
  max-height: 94%;
  overflow-y: auto;
  animation: slide-up 0.38s cubic-bezier(0.22, 1, 0.36, 1);
}

@keyframes slide-up { from { transform: translateY(100%); } to { transform: none; } }

.grab { width: 36px; height: 4px; border-radius: 2px; background: var(--line-2); margin: -6px auto 0; }

header { display: flex; align-items: center; gap: 11px; }

.crest {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  flex: none;
  display: grid;
  place-items: center;
  background: linear-gradient(140deg, var(--active), var(--magenta));
  color: var(--text);
  box-shadow: var(--cta-shadow);
}

header h3 {
  margin: 0;
  font-family: var(--f-title);
  font-size: 20px;
  font-variation-settings: 'wdth' 112, 'wght' 800;
}

header p { margin: 0; font-size: 12.5px; color: var(--text-2); }

/* Quatro abas não cabem lado a lado sem quebrar o rótulo em duas linhas; elas rolam. */
.tabs {
  display: flex;
  gap: 6px;
  padding: 4px;
  overflow-x: auto;
  scrollbar-width: none;
  border-radius: var(--r-pill);
  background: var(--surface-2);
  border: 1px solid var(--line);
}

.tab {
  flex: 0 0 auto;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 7px 8px;
  border: none;
  border-radius: var(--r-pill);
  background: none;
  color: var(--text-3);
  font-family: var(--f-label);
  font-size: 9.5px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  cursor: pointer;
  transition: background 0.16s, color 0.16s;
}

.tab.on { background: var(--surface); color: var(--active); box-shadow: var(--card-shadow); }

.steps { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 12px; }

.steps li { display: flex; gap: 11px; font-size: 13.5px; color: var(--text-2); line-height: 1.5; }

.steps b { color: var(--text); display: block; }
.steps :deep(i) { color: var(--active); font-style: normal; font-weight: 600; }

.n {
  width: 24px;
  height: 24px;
  flex: none;
  border-radius: 50%;
  display: grid;
  place-items: center;
  background: var(--active-veil);
  color: var(--active);
  font-family: var(--f-label);
  font-size: 11px;
  font-weight: 700;
}

.block {
  padding: 13px 14px;
  border-radius: var(--r-button);
  background: var(--surface-2);
  border: 1px solid var(--line);
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.block p { margin: 0; font-size: 12.5px; color: var(--text-2); line-height: 1.5; }
.block b { color: var(--text); }
.warn { color: var(--amber); }

.mode { display: flex; align-items: flex-start; gap: 8px; }
.empty { color: var(--text-3); }

.formula {
  font-family: var(--f-label);
  font-size: 13px;
  color: var(--text);
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.chip-good {
  background: var(--ok-veil);
  color: var(--ok);
  padding: 3px 8px;
  border-radius: var(--r-pill);
  font-size: 11px;
}

.credit { display: block; text-align: center; }
.credit a { color: var(--active); }

.actions { display: flex; gap: 10px; }
.actions .btn { flex: 1; }

@media (min-width: 1024px) {
  .scrim { align-items: center; justify-content: center; }
  .sheet {
    max-width: 460px;
    border-radius: var(--r-card-lg);
    max-height: 88%;
    animation: zoom-in 0.34s cubic-bezier(0.22, 1, 0.36, 1);
  }
  .grab { display: none; }
}

@keyframes zoom-in {
  from { transform: scale(0.9); opacity: 0; }
  to { transform: none; opacity: 1; }
}
</style>
